import { useCallback, useEffect, useRef, useState } from 'react';

import { isViteDevServer } from '@/utilities/helpers/isViteDevServer';

export const PRECHECK_STATE = {
  CHECKING: 'checking',
  COMPLETE: 'complete',
} as const;

export type PrecheckState =
  (typeof PRECHECK_STATE)[keyof typeof PRECHECK_STATE];

const GEOLOCATION_OPTIONS = {
  timeout: 10000,
  maximumAge: Infinity,
} as const;

const PERMISSION_POLL_INTERVAL_MS = 500;
const PERMISSION_POLL_ATTEMPTS = 20;

/** How long to wait for each PLC port probe before aborting. */
const PLC_PROBE_TIMEOUT_MS = 3000;

// PLC ports in the CSP allowlist (connect-src). Port-free http://127.0.0.1 is blocked by CSP.
// CSP varies by environment — try each port until one gets past CSP (connection refused is fine;
// Chrome shows the LNA popup before the TCP connection attempt).
export const PLC_PORTS = [53280, 53281, 53282] as const;

const LOCAL_NETWORK_ACCESS = 'local-network-access' as const;

const waitForPromptResolution = (
  status: PermissionStatus,
  trigger?: (resolve: () => void) => void,
): Promise<void> =>
  new Promise((resolve) => {
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;

    const finish = () => {
      if (settled) return;

      settled = true;
      if (timer) clearTimeout(timer);
      status.removeEventListener?.('change', finish);
      resolve();
    };

    const poll = () => {
      if (settled) return;

      if (status.state !== 'prompt' || attempts >= PERMISSION_POLL_ATTEMPTS) {
        finish();
        return;
      }

      attempts += 1;
      timer = setTimeout(poll, PERMISSION_POLL_INTERVAL_MS);
    };

    status.addEventListener?.('change', finish, { once: true });
    trigger?.(finish);
    poll();
  });

/**
 * Warms up HTML5 geolocation before the GeoComply SDK loads when the Permissions
 * API is available. If 'prompt', triggers getCurrentPosition to show a working
 * popup before heavy SDK scripts run. If 'granted', establishes an active geo
 * session in the top-level frame (iframe inheritance). If 'denied', skips.
 * Browsers without Permissions API (older Safari) no-op here; see transient 803 retry.
 */
async function checkGeolocationPermission(): Promise<void> {
  if (
    !navigator.geolocation ||
    !(window as Window & { chrome?: unknown }).chrome
  ) {
    return;
  }

  try {
    const status = await navigator.permissions.query({
      name: 'geolocation',
    });
    // 'denied': no popup possible, skip immediately.
    // 'granted'/'prompt': call getCurrentPosition. For 'granted' it resolves from cache
    // (maximumAge: Infinity) to establish an active geo session in the top-level frame,
    // ensuring game client iframes inherit the confirmed permission. For 'prompt' it
    // triggers the browser popup before SDK scripts are loaded.
    if (status.state === 'denied') {
      return;
    }
    const requestPosition = (resolve: () => void) =>
      navigator.geolocation.getCurrentPosition(
        resolve,
        resolve,
        GEOLOCATION_OPTIONS,
      );

    if (status.state === 'prompt') {
      await waitForPromptResolution(status, requestPosition);
    } else {
      await new Promise<void>(requestPosition);
    }
  } catch {
    return;
  }
}

/**
 * Checks local network access permission state (Chrome 142+).
 * If 'prompt', triggers a fetch to localhost to show Chrome's "Access apps and services" popup.
 * Safari/Firefox: no LNA permission, resolves immediately.
 */
async function checkLocalNetworkPermission(): Promise<void> {
  try {
    const status = await navigator.permissions.query({
      name: LOCAL_NETWORK_ACCESS as PermissionName,
    });

    if (status.state === 'granted' || status.state === 'denied') {
      return;
    }

    // Vite dev server: query permission state but skip PLC probe + popup wait.
    // PLC is not running locally; probing localhost blocks precheck on cert env.
    if (isViteDevServer()) {
      return;
    }

    // 'prompt': trigger the Chrome LNA popup via a fetch to localhost.
    // The fetch fails immediately (connection refused — PLC not running), but that's fine —
    // Chrome shows the LNA popup before the TCP connection attempt.
    // CSP varies by environment — try each PLC port until one gets past CSP.
    for (const port of PLC_PORTS) {
      let cspBlocked = false;
      const onCspViolation = () => {
        cspBlocked = true;
      };
      document.addEventListener('securitypolicyviolation', onCspViolation, {
        once: true,
      });

      try {
        await fetch(`http://127.0.0.1:${port}`, {
          method: 'HEAD',
          signal: AbortSignal.timeout(PLC_PROBE_TIMEOUT_MS),
        });
      } catch {
        // Expected: connection refused or permission denied
      } finally {
        document.removeEventListener('securitypolicyviolation', onCspViolation);
      }

      if (!cspBlocked) break;
    }

    // Poll while the popup is unresolved, then continue to the next precheck after
    // ten seconds rather than leaving the player blocked for a full minute.
    if (status.state === 'prompt') {
      await waitForPromptResolution(status);
    }
  } catch {
    // Safari/Firefox: 'local-network-access' not supported — no popup needed
  }
}

/**
 * Pre-checks geolocation + local network permissions before the GeoComply SDK loads.
 *
 * Chrome's permission popups become unresponsive when GeoComply SDK scripts are loaded
 * or when 1-second interval timers (e.g. the session/game timers) are running. By
 * triggering the popups before any of that is active, the popups respond to clicks
 * normally. Also probes the PLC ports (53280-53282) so PLC presence can be detected.
 *
 * @param options.skip - Skip pre-checks entirely (for native wrappers, where the host
 *   app owns permissions and there is no browser popup to coordinate with).
 */
export const usePermissionPrecheck = ({
  skip = false,
}: { skip?: boolean } = {}) => {
  const [state, setState] = useState<PrecheckState>(
    skip ? PRECHECK_STATE.COMPLETE : PRECHECK_STATE.CHECKING,
  );
  const hasRun = useRef(false);

  const runChecks = useCallback(async () => {
    if (skip) return;

    setState(PRECHECK_STATE.CHECKING);

    // Sequential: Chrome can only show one permission bubble at a time.
    await checkGeolocationPermission();
    await checkLocalNetworkPermission();

    setState(PRECHECK_STATE.COMPLETE);
  }, [skip]);

  useEffect(() => {
    if (!skip && !hasRun.current) {
      hasRun.current = true;
      // Defer off the render commit: runChecks calls setState, which must not run
      // synchronously inside the effect body.
      queueMicrotask(() => {
        void runChecks();
      });
    }
  }, [runChecks, skip]);

  return { state, recheckPermissions: runChecks };
};
